import { Injectable } from "@angular/core";
import { BiometricAuth } from "@aparajita/capacitor-biometric-auth";
import {
  CheckBiometryResult,
  BiometryError,
  BiometryErrorType,
  BiometryType,
} from "@aparajita/capacitor-biometric-auth";
import { Capacitor } from "@capacitor/core";

export interface BiometricAuthResult {
  success: boolean;
  error?: string;
  errorCode?: BiometryErrorType;
}

export interface BiometricAvailability {
  isAvailable: boolean;
  biometryType?: BiometryType;
  reason?: string;
}

@Injectable({
  providedIn: "root",
})
export class BiometricAuthService {
  constructor() {}

  async checkBiometricAvailability(): Promise<BiometricAvailability> {
    if (!Capacitor.isNativePlatform()) {
      return {
        isAvailable: false,
        reason:
          "Biometric authentication is only available on native platforms",
      };
    }

    try {
      const result: CheckBiometryResult = await BiometricAuth.checkBiometry();

      return {
        isAvailable: result.isAvailable,
        biometryType: result.biometryType,
        reason: result.reason,
      };
    } catch (error) {
      console.error("Error checking biometric availability:", error);
      return {
        isAvailable: false,
        reason: "Error checking biometric availability",
      };
    }
  }

  async authenticate(
    reason: string = "Please authenticate to continue"
  ): Promise<BiometricAuthResult> {
    try {
      await BiometricAuth.authenticate({
        reason: reason,
        cancelTitle: "Cancel",
        allowDeviceCredential: true,
        iosFallbackTitle: "Use Passcode",
        androidTitle: "Biometric Login",
        androidSubtitle: "Log in using biometric authentication",
        androidConfirmationRequired: false,
      });

      return {
        success: true,
      };
    } catch (error) {
      if (error instanceof BiometryError) {
        return {
          success: false,
          error: error.message,
          errorCode: error.code,
        };
      }

      return {
        success: false,
        error: "An unknown error occurred during authentication",
      };
    }
  }

  getBiometryTypeName(type?: BiometryType): string {
    if (!type) return "Biometric";

    switch (type) {
      case BiometryType.fingerprintAuthentication:
        return "Fingerprint";
      case BiometryType.faceAuthentication:
        return "Face ID";
      case BiometryType.irisAuthentication:
        return "Iris";
      case BiometryType.touchId:
        return "Touch ID";
      case BiometryType.faceId:
        return "Face ID";
      default:
        return "Biometric";
    }
  }
}
